import React, { useRef, useState } from 'react';
import ReCAPTCHADefault from 'react-google-recaptcha';
import { submitContactForm } from '../../api/contact';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const interestOptions = [
  {
    name: 'softwareDelivery',
    label: 'Software delivery',
  },
  {
    name: 'aiAutomation',
    label: 'AI / automation',
  },
  {
    name: 'productSupport',
    label: 'Product support',
  },
  {
    name: 'partnership',
    label: 'Partnership',
  },
];

export default function ContactForm() {
  const recaptchaRef = useRef(null);

  const [captchaToken, setCaptchaToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    message: '',
    consent: false,
    softwareDelivery: false,
    aiAutomation: false,
    productSupport: false,
    partnership: false,
  });

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      organisation: '',
      message: '',
      consent: false,
      softwareDelivery: false,
      aiAutomation: false,
      productSupport: false,
      partnership: false,
    });

    setCaptchaToken(null);

    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!captchaToken) {
      alert('Please complete the CAPTCHA to submit the form.');
      return;
    }

    try {
      setIsSubmitting(true);

      await submitContactForm({
        ...formData,
        captchaToken,
      });

      alert('Thank you. Your message has been received.');
      resetForm();
    } catch (error) {
      console.error('Contact form error:', error);
      alert(error.message || 'Unable to submit the form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section contact-section">
      <div className="contact-grid">
        <div>
          <p className="eyebrow">Contact</p>
          <h2>Let’s discuss your technology delivery needs.</h2>
          <p>
            For project enquiries, partnerships or delivery support, send us a message and we’ll
            get back to you.
          </p>

          <div className="email-card">
            <strong>Prefer email?</strong>
            <a href="mailto:info@arthanovaglobal.com">info@arthanovaglobal.com</a>
          </div>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row two">
            <div>
              <label htmlFor="name">Name*</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label htmlFor="email">Email*</label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <label htmlFor="organisation">Organisation</label>
            <input
              id="organisation"
              name="organisation"
              type="text"
              value={formData.organisation}
              onChange={handleChange}
            />
          </div>

          <div>
            <p className="form-label">Interest</p>
            <div className="interest-grid">
              {interestOptions.map((option) => (
                <label key={option.name} className="interest-option">
                  <input
                    type="checkbox"
                    name={option.name}
                    checked={formData[option.name]}
                    onChange={handleChange}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-row">
            <label htmlFor="message">Message*</label>
            <textarea
              id="message"
              name="message"
              rows="5"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your project, delivery need or partnership idea."
              required
            />
          </div>

          <label className="consent">
            <input
              type="checkbox"
              name="consent"
              checked={formData.consent}
              onChange={handleChange}
              required
            />
            <span>
              I agree that this data will be stored and processed so Arthanova Global can respond
              to my enquiry.*
            </span>
          </label>

          <div className="captcha-box">
            {RECAPTCHA_SITE_KEY ? (
              <ReCAPTCHADefault
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => setCaptchaToken(token)}
              />
            ) : (
              <p className="captcha-warning">reCAPTCHA is not configured.</p>
            )}
          </div>

          <button type="submit" disabled={isSubmitting} className="submit-btn">
            {isSubmitting ? 'Sending...' : 'Send message'}
          </button>
        </form>
      </div>
    </section>
  );
}